import { HistoriaLimitu } from '@/components/HistoriaLimitu';
import { OknoLimituImiennego } from '@/components/OknoLimituImiennego';
import { OknoLimituModal } from '@/components/OknoLimituModal';
import { OknoPotwierdzenia } from '@/components/OknoPotwierdzenia';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ZderzeniaLimitow } from '@/components/ZderzeniaLimitow';
import { AlertTriangle, ChevronLeft, ChevronRight, Clock, History, Info, Loader2, Lock, Plus, Search, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fetchMembers } from '@/lib/employee-groups-api';
import { usunLimitReguly, utworzLimitImienny, zmienLimitReguly } from '@/lib/rationing-api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { OSOBY, UPRAWNIENIA, zLiczba } from '@/lib/odmiana';
import { cn } from '@/lib/utils';
import {
  ETYKIETA_ADRESATA,
  ETYKIETA_ZAKRESU,
  fetchPermissionOptions,
  fetchSkutekOdebrania,
  grantPermission,
  JEDNOSTKI_OKNA,
  listPermissions,
  removePermissionLimit,
  revokePermission,
  setPermissionLimit,
  type Adresat,
  type Flaga,
  type StanFlagi,
  type Uprawnienie,
} from '@/lib/permissions-api';

/**
 * UPRAWNIENIA DO PRODUKTÓW — jeden ekran na dwie tabele SPS.
 *
 * ⚠️ DLACZEGO JEDEN, skoro w SupplyPointcie to dwa niezależne mechanizmy: bo użytkowe znaczenie
 * mają wyłącznie razem. Limit bez wpisu dostępu nie ma czego ograniczać, więc operator, który
 * ustawia jedno bez drugiego, dostaje regułę, która nie działa — i nie ma jak tego zobaczyć,
 * dopóki są to dwa osobne ekrany. Zmierzone na DBW: 115 wpisów dostępu, 51 z limitem, ZERO
 * limitów bez dostępu.
 *
 * ⚠️ TEN EKRAN WCHŁANIA DOTYCHCZASOWE „OGRANICZENIA" (moduł `limits`) i dlatego chodzi pod tym
 * samym uprawnieniem — dołożenie nowego znaczyłoby, że aktualizacja odbiera dostęp do ekranu,
 * którego nikt nikomu nie odbierał.
 */

const STAN_ETYKIETA: Record<StanFlagi, string> = {
  WLACZONE: 'WŁĄCZONE',
  WYLACZONE: 'WYŁĄCZONE',
  ROZNI_SIE: 'RÓŻNI SIĘ',
  NIEUSTALONE: 'NIEUSTALONE',
};

/**
 * Kolor mówi o RYZYKU POMYŁKI, nie o wartości flagi.
 *
 * Zielone jest tylko to, co wiadomo na pewno i na wszystkich automatach. „Różni się" jest
 * ostrzeżeniem, bo ta sama lista działa wtedy na jednej maszynie, a na drugiej nie —
 * a „nieustalone" jest ostrzeżeniem, bo brak wiedzy łatwo przeczytać jako „wyłączone".
 */
function PlakietkaFlagi({ f }: { f: Flaga }) {
  const wariant = f.stan === 'WLACZONE' || f.stan === 'WYLACZONE' ? 'default' : 'warning';
  const tytul =
    f.stan === 'ROZNI_SIE'
      ? f.per.map((p) => `${p.nazwa ?? `VMID ${p.vmid}`}: ${p.wlaczone ? 'włączone' : 'wyłączone'}`).join('\n')
      : (f.powod ?? (f.opis ?? undefined));
  return (
    <Badge variant={wariant} title={tytul}>
      {STAN_ETYKIETA[f.stan]}
      {f.stan === 'ROZNI_SIE' ? ` (${f.wlaczonych}/${f.automatow})` : ''}
    </Badge>
  );
}


/**
 * NADANIE DOSTĘPU — z limitem albo bez.
 *
 * ⚠️ „Bez limitu" to PEŁNY, nieograniczony dostęp i tak jest napisane na przycisku.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ DOMYŚLNIE OKNO POKAZUJE OD RAZU LIMIT, a odznaczenie jest świadomym „bez limitu" —
 *  ROZSTRZYGNIĘTE PRZEZ UŻYTKOWNIKA (28.08.2026): „w nadaniu dostępu domyślnie ma być odwrotna
 *  logika, tzn. ma być od razu pokazana ilość sztuk w zadanym czasie, a checkbox ma być «bez
 *  limitu»".
 *
 *  ⚠️ ODWRACA WCZEŚNIEJSZĄ DECYZJĘ ZAPISANĄ W TYM MIEJSCU, więc zostawiam po niej ślad razem
 *  z powodem: pola limitu były schowane, „bo taki wpis jest w danych klienta częstszy (na DBW
 *  64 ze 115) i bo dołożenie limitu później jest jednym kliknięciem, a odkręcanie limitu nadanego
 *  przez przypadek — nie". Ryzyko przypadkowego limitu zostaje i jest zaadresowane NAPISEM NA
 *  PRZYCISKU: mówi wprost „z limitem" albo „bez limitu", więc stan widać w miejscu, w które
 *  patrzy się przed kliknięciem.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * TREŚĆ PYTANIA „ODBIERZ DOSTĘP" — osobno dla wpisu „wszyscy" i „wszystkie produkty" (28.09.2026).
 *
 * ⚠️ WPIS „WSZYSCY" DOTYCZY CAŁEJ WYSPY i pytanie ma to powiedzieć wprost; ile osób zostanie bez
 * niczego, dopowiada `SkutekOdebrania` pod treścią.
 */
function trescOdebrania(u: Uprawnienie, a: Adresat): string {
  const zLimitem = u.limit ? ' RAZEM Z LIMITEM' : '';
  if (a.kind === 'all') {
    return (
      `Odebrać wszystkim pracownikom dostęp do „${u.zakres.name}"${zLimitem}?\n\n` +
      'To wpis całej wyspy. Po zastosowaniu na automatach dostęp zostanie tylko tym, którzy mają własny wpis albo wpis swojej grupy.'
    );
  }
  if (u.zakres.kind === 'all') {
    return (
      `Odebrać dostęp do wszystkich produktów dla „${a.name}"${zLimitem}?\n\n` +
      'Po zastosowaniu na automatach zostanie tylko to, do czego jest osobny wpis.'
    );
  }
  return (
    `Odebrać dostęp do „${u.zakres.name}" dla „${a.name}"${zLimitem}?\n\n` +
    'Po zastosowaniu na automatach ten adresat przestanie pobierać ten produkt.'
  );
}

/** Ile osób zostanie bez ŻADNEGO wpisu dostępu — liczy serwer, na żądanie. */
function SkutekOdebrania({ id, groupId }: { id: string; groupId: string }) {
  const q = useQuery({ queryKey: ['skutek-odebrania', id], queryFn: () => fetchSkutekOdebrania(id, groupId) });
  if (q.isPending) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Liczę, kto zostanie bez dostępu…
      </p>
    );
  }
  if (q.isError) return <p className="text-xs text-[var(--destructive)]">Nie udało się policzyć, kto zostanie bez dostępu.</p>;
  const { bezDostepu, wszystkich } = q.data;
  return (
    <p className={bezDostepu > 0 ? 'text-sm font-medium text-[var(--destructive)]' : 'text-sm text-muted-foreground'}>
      Bez żadnego dostępu zostanie {bezDostepu} z {wszystkich} osób.
    </p>
  );
}

function NadajDostepModal({ groupId, onClose, onSaved }: { groupId: string; onClose: () => void; onSaved: () => void }) {
  const [kimJest, setKimJest] = useState<'group' | 'employee' | 'all'>('group');
  const [coTo, setCoTo] = useState<'itemGroup' | 'item' | 'all'>('itemGroup');
  /** Drugie kliknięcie przy „wszyscy → wszystkie produkty" — patrz niżej przy przycisku. */
  const [potwierdzWszystko, setPotwierdzWszystko] = useState(false);
  const [celId, setCelId] = useState('');
  const [zakresId, setZakresId] = useState('');
  /** ⚠️ `true` NA START — patrz komentarz nad komponentem. Checkbox pyta o ODWROTNOŚĆ. */
  const [zLimitem, setZLimitem] = useState(true);
  const [maxQty, setMaxQty] = useState('1');
  const [period, setPeriod] = useState('1');
  const [periodType, setPeriodType] = useState('3');
  const [szukaj, setSzukaj] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const [zapisuje, setZapisuje] = useState(false);

  const opcje = useQuery({
    queryKey: ['permission-options', groupId, szukaj],
    queryFn: () => fetchPermissionOptions(groupId, szukaj || undefined),
  });

  const wybranyPracownik = opcje.data?.employees.find((e) => e.id === celId);
  /**
   * ⚠️ `RATIONEFFECT` INNE NIŻ 3 ZNACZY, ŻE AUTOMAT NIE PILNUJE LIMITÓW TEJ OSOBY. Nadanie jej
   * limitu obiecywałoby ograniczenie, którego nikt nie egzekwuje — serwer i tak odmówi, ale
   * lepiej powiedzieć to przed kliknięciem niż komunikatem błędu po.
   */
  const limityIgnorowane = kimJest === 'employee' && wybranyPracownik !== undefined && wybranyPracownik.rationEffect !== 3;
  /**
   * ⚠️ PRZY „WSZYSTKICH" TYLKO BEZ LIMITU (28.09.2026). Tabela limitów SPS zna wyłącznie osobę albo
   * grupę i produkt albo grupę produktów — serwer i tak odmówi, więc formularz nie proponuje limitu.
   */
  const wszystkich = kimJest === 'all' || coTo === 'all';
  const zLimitemNaPewno = zLimitem && !wszystkich;
  const wszyscyWszystko = kimJest === 'all' && coTo === 'all';

  const zapisz = async () => {
    setBlad(null);
    setZapisuje(true);
    try {
      await grantPermission({
        groupId,
        ...(kimJest === 'all'
          ? { allEmployees: true as const }
          : kimJest === 'group'
            ? { employeeGroupId: celId }
            : { employeeId: celId }),
        ...(coTo === 'all' ? { allItems: true as const } : coTo === 'itemGroup' ? { itemGroupId: zakresId } : { itemId: zakresId }),
        ...(zLimitemNaPewno
          ? { limit: { maxQty: Number(maxQty), period: Number(period), periodType: Number(periodType) } }
          : {}),
      });
      onSaved();
      onClose();
    } catch (e) {
      setBlad(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title="Nadaj dostęp do produktu"
        description="Dostęp mówi, CO adresat może pobierać. Limit (opcjonalny) mówi, ILE — i bez wpisu dostępu nie ma czego ograniczać."
      >
        <FormGrid>
          <Field label="Komu" htmlFor="kim" required>
            <div className="flex gap-2">
              <Select
                value={kimJest}
                onChange={(e) => {
                  setKimJest(e.target.value as 'group' | 'employee' | 'all');
                  setCelId('');
                  setPotwierdzWszystko(false);
                }}
                className="w-44"
              >
                <option value="group">Grupa pracowników</option>
                <option value="employee">Pracownik</option>
                <option value="all">Wszyscy pracownicy</option>
              </Select>
              {kimJest !== 'all' && (
              <Select id="kim" value={celId} onChange={(e) => setCelId(e.target.value)} className="flex-1">
                <option value="">— wybierz —</option>
                {kimJest === 'group'
                  ? opcje.data?.employeeGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.members} os.)
                      </option>
                    ))
                  : opcje.data?.employees.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name} · {e.spsUserNumber}
                      </option>
                    ))}
              </Select>
              )}
            </div>
          </Field>

          {kimJest === 'employee' && (
            <p className="-mt-1 text-xs text-muted-foreground">
              Lista pokazuje 50 osób — zawęź szukajką, jeśli kogoś brakuje.
              <Input
                className="mt-1 h-8"
                placeholder="Szukaj pracownika albo produktu…"
                value={szukaj}
                onChange={(e) => setSzukaj(e.target.value)}
              />
            </p>
          )}

          <Field label="Do czego" htmlFor="co" required>
            <div className="flex gap-2">
              <Select
                value={coTo}
                onChange={(e) => {
                  setCoTo(e.target.value as 'itemGroup' | 'item' | 'all');
                  setZakresId('');
                  setPotwierdzWszystko(false);
                }}
                className="w-44"
              >
                <option value="itemGroup">Grupa produktów</option>
                <option value="item">Pojedynczy produkt</option>
                <option value="all">Wszystkie produkty</option>
              </Select>
              {coTo !== 'all' && (
              <Select id="co" value={zakresId} onChange={(e) => setZakresId(e.target.value)} className="flex-1">
                <option value="">— wybierz —</option>
                {coTo === 'itemGroup'
                  ? opcje.data?.itemGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.items})
                      </option>
                    ))
                  : opcje.data?.items.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} · {i.itemNumber}
                      </option>
                    ))}
              </Select>
              )}
            </div>
          </Field>

          {/*
            ⚠️ CHECKBOX PYTA O ODWROTNOŚĆ STANU, który trzyma komponent — i to jest celowe.
            Formularz ma jedno pytanie „czy z limitem", a stan `zLimitem` odpowiada na nie w całym
            pliku (payload, ostrzeżenie o `RATIONEFFECT`, napis na przycisku). Druga zmienna na tę
            samą rzecz rozjeżdżałaby się przy pierwszej poprawce, więc odwracamy raz, tutaj.
          */}
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-3.5 accent-[var(--primary)]"
              checked={!zLimitemNaPewno}
              disabled={wszystkich}
              onChange={(e) => setZLimitem(!e.target.checked)}
            />
            Bez limitu (dostęp nieograniczony)
          </label>
          {wszystkich && <p className="-mt-1 text-xs text-muted-foreground">Przy „wszystkich” tylko bez limitu.</p>}

          {zLimitemNaPewno && (
            <div className="flex items-end gap-2">
              <Field label="Ile sztuk" htmlFor="q">
                <Input id="q" type="number" min={0} className="w-24" value={maxQty} onChange={(e) => setMaxQty(e.target.value)} />
              </Field>
              <Field label="W czasie" htmlFor="p">
                <Input id="p" type="number" min={0} step="any" className="w-24" value={period} onChange={(e) => setPeriod(e.target.value)} />
              </Field>
              <Select value={periodType} onChange={(e) => setPeriodType(e.target.value)} className="mb-0.5 flex-1">
                {JEDNOSTKI_OKNA.map((j) => (
                  <option key={j.v} value={j.v}>
                    {j.l}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </FormGrid>

        {limityIgnorowane && zLimitemNaPewno && (
          <p className="mt-2 flex items-start gap-1.5 rounded border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--warning)]" />
            <span>
              „{wybranyPracownik?.name}" ma w SPS wyłączone egzekwowanie limitów (RATIONEFFECT ={' '}
              {wybranyPracownik?.rationEffect}), więc automat i tak by tej reguły nie pilnował. Najpierw włącz
              egzekwowanie na karcie pracownika.
            </span>
          </p>
        )}

        {/*
          ⚠️ „WSZYSCY → WSZYSTKIE PRODUKTY" DOPIERO ZA DRUGIM KLIKNIĘCIEM — to wpis całej wyspy:
          każdy pracownik dostaje dostęp do wszystkiego na każdym automacie.
        */}
        {wszyscyWszystko && potwierdzWszystko && (
          <p className="mt-2 flex items-start gap-1.5 rounded border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--warning)]" />
            <span>Każdy pracownik wyspy dostanie dostęp do wszystkich produktów na każdym automacie.</span>
          </p>
        )}

        {blad && <ModalError>{blad}</ModalError>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={zapisuje}>
            Anuluj
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (wszyscyWszystko && !potwierdzWszystko) {
                setPotwierdzWszystko(true);
                return;
              }
              void zapisz();
            }}
            disabled={
              (kimJest !== 'all' && celId === '') ||
              (coTo !== 'all' && zakresId === '') ||
              zapisuje ||
              (zLimitemNaPewno && limityIgnorowane)
            }
          >
            {zapisuje && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {wszyscyWszystko && potwierdzWszystko
              ? 'Tak, nadaj wszystkim'
              : zLimitemNaPewno
                ? 'Nadaj dostęp z limitem'
                : 'Nadaj dostęp bez limitu'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * KTO NALEŻY DO GRUPY, DLA KTOREJ NADANO UPRAWNIENIE.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ ZGŁOSZONE (28.08.2026): „fajnie jakby dało się tu kliknąć na osoby i zobaczyć listę, kto ma
 *  dane uprawnienie". Nagłówek mówił „17 os." i na tym się kończyło — a uprawnienie nadane grupie
 *  dotyczy KONKRETNYCH ludzi i to o nich jest pytanie przy każdej reklamacji „dlaczego on pobrał".
 *
 *  ⚠️ BEZ NOWEGO ENDPOINTU: `GET /employee-groups/:id/members` istnieje i oddaje dokładnie to,
 *  czego tu trzeba, razem z `rationEffect`.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function SkladGrupy({ adresat, onClose }: { adresat: Adresat; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['employee-group-members', adresat.id],
    queryFn: () => fetchMembers(adresat.id!),
    enabled: adresat.id !== null,
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={`Skład grupy „${adresat.name}"`}
        description="Uprawnienie nadane grupie działa na te osoby. Lista pochodzi z read-modelu portalu."
      >
        {q.isLoading && (
          <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Wczytywanie…
          </p>
        )}
        {q.error && <ModalError>Nie udało się wczytać składu grupy.</ModalError>}
        {q.data && q.data.members.length === 0 && (
          <p className="py-4 text-sm text-muted-foreground">
            Ta grupa nie ma ani jednego członka — uprawnienie nie działa dziś na nikogo.
          </p>
        )}
        {q.data && q.data.members.length > 0 && (
          <>
            <p className="mb-2 text-xs text-muted-foreground">{zLiczba(q.data.members.length, OSOBY)}</p>
            {/* ⚠️ WYSOKOŚĆ OGRANICZONA I PRZEWIJANA: grupa „FIRMA ZEWNETRZNA" ma na PESA kilkadziesiąt
                osób, a modal bez tego rósłby poza ekran razem z przyciskiem zamknięcia. */}
            <ul className="max-h-[50vh] divide-y divide-border overflow-y-auto rounded border border-border">
              {q.data.members.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5 text-sm">
                  <span className="font-medium">{m.name}</span>
                  <code className="text-[11px] text-muted-foreground">{m.spsUserNumber}</code>
                  {!m.active && (
                    <Badge variant="neutral" title="Konto nieaktywne w SPS">
                      nieaktywny
                    </Badge>
                  )}
                  {/*
                    ⚠️ TO SAMO OSTRZEŻENIE, CO PRZY NADAWANIU LIMITU. `RATIONEFFECT` inne niż 3 znaczy,
                    że automat NIE pilnuje limitów tej osoby — więc limit wystawiony na grupę jej nie
                    dotyczy, choć jest w grupie. Bez tego lista odpowiadałaby na pytanie „kto ma
                    uprawnienie" i milczała o tym, u kogo limit nie działa.
                  */}
                  {m.rationEffect !== 3 && (
                    <Badge variant="warning" title={`RATIONEFFECT = ${m.rationEffect}`}>
                      limity nie egzekwowane
                    </Badge>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {m.machineCount === 0 ? 'bez automatów' : `automatów: ${m.machineCount}`}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Zamknij
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Ilu adresatów (osób albo grup z kompletem uprawnień) na stronę — sekcje są zwinięte, więc to są nagłówki. */
const ADRESATOW_NA_STRONE = 100;

function Strony({
  strona,
  naStronie,
  adresatow,
  laduje,
  zmien,
}: {
  strona: number;
  naStronie: number;
  adresatow: number;
  laduje: boolean;
  zmien: (s: number) => void;
}) {
  const stron = Math.max(1, Math.ceil(adresatow / naStronie));
  const od = (strona - 1) * naStronie + 1;
  const doN = Math.min(adresatow, strona * naStronie);
  return (
    <div className="mt-3 flex items-center justify-end gap-3 text-sm text-muted-foreground">
      {laduje && <Loader2 className="h-4 w-4 animate-spin" />}
      <span className="tabular-nums">
        Adresaci {od}–{doN} z {adresatow}
      </span>
      <Button variant="outline" size="sm" disabled={strona <= 1} onClick={() => zmien(strona - 1)} aria-label="Poprzednia strona">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="tabular-nums">
        {strona} / {stron}
      </span>
      <Button variant="outline" size="sm" disabled={strona >= stron} onClick={() => zmien(strona + 1)} aria-label="Następna strona">
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function Permissions() {
  const { t } = useI18n();
  const { groupId, island } = useIslands();
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [target, setTarget] = useState<'all' | 'employees' | 'groups'>('all');
  const [mode, setMode] = useState<'all' | 'nolimit' | 'limit'>('all');
  /**
   * ⚠️⚠️ STRONA ADRESATÓW (14.10.2026, Adient: ponad 5000 wpisów dostępu → API odmawiało całej listy
   * i ekran stał pusty). Strona to osoby i grupy z KOMPLETEM ich uprawnień — adresat nigdy nie jest
   * rozcięty na dwie strony. Każda zmiana filtrów wraca na pierwszą.
   */
  const [strona, setStrona] = useState(1);
  const [rozwinFlagi, setRozwinFlagi] = useState(false);
  const [nadaje, setNadaje] = useState(false);
  /** Uprawnienie, dla którego otwarto okno limitu — razem z nazwą adresata do tytułu. */
  const [edytowany, setEdytowany] = useState<{ u: Uprawnienie; a: Adresat } | null>(null);
  /**
   * PYTANIA PRZED ZDJĘCIEM LIMITU I PRZED ODEBRANIEM DOSTĘPU.
   *
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️⚠️ „PRZYPISZ NOLIMIT" NIE PYTAŁ O NIC (zgłoszone 02.09.2026: „teraz można przez przypadek
   *  kliknąć i stare ustawienie idzie w pizdu"). Przycisk stoi w rzędzie obok „Zmień limit",
   *  ma ten sam rozmiar i wygląd, a robi rzecz nieodwracalną: kasuje wiersz `Rationing`.
   *  Przywrócenie znaczy nadanie limitu od nowa — trzeba wiedzieć, ile było.
   *
   *  ⚠️ PYTANIE WYMIENIA STARY LIMIT, bo to jest ta informacja, która ginie. „Zdjąć limit?" bez
   *  liczby nie pozwala nawet ocenić, czy kliknięcie było pomyłką.
   *
   *  ⚠️ „ODBIERZ DOSTĘP" DOSTAJE TO SAMO OKNO. Miał `confirm()` przeglądarki i po zamianie
   *  sąsiada wyglądałoby to jak awaria: dwa przyciski w jednym rzędzie, jeden pyta w portalu,
   *  drugi alertem z adresem strony i opcją „nie pytaj więcej".
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const [doZdjeciaLimitu, setDoZdjeciaLimitu] = useState<{ u: Uprawnienie; a: Adresat } | null>(null);
  const [doOdebrania, setDoOdebrania] = useState<{ u: Uprawnienie; a: Adresat } | null>(null);
  /**
   * ⚠️⚠️ OSOBNY STAN DLA USUNIĘCIA SAMEJ REGUŁY. Wiersz bez wpisu dostępu nie ma czego odebrać,
   * a jego `id` wskazuje REGUŁĘ limitu — więc i potwierdzenie, i trasa są inne niż przy zwykłym
   * wierszu. Jeden wspólny stan znaczyłby, że o wyborze trasy decyduje pole w środku — i przy
   * pierwszej poprawce ktoś wysłałby identyfikator reguły pod adres uprawnienia.
   */
  const [doUsunieciaReguly, setDoUsunieciaReguly] = useState<{ u: Uprawnienie; a: Adresat } | null>(null);
  /** Nowy limit imienny — zamówione 07.09.2026: „wszystko ma być w limitach". */
  const [dodajLimitDla, setDodajLimitDla] = useState<Adresat | null>(null);
  /** Limit, którego historię otwarto — razem z nazwami do nagłówka okna. */
  const [historia, setHistoria] = useState<{ u: Uprawnienie; a: Adresat } | null>(null);
  const [pracuje, setPracuje] = useState<string | null>(null);
  /**
   * KTORE SEKCJE SĄ ROZWINIĘTE — domyślnie ŻADNA.
   *
   * ⚠️ ZGŁOSZONE (28.08.2026): „zróbmy te grupy jako zwinięte, żeby prościej się szukało grupy.
   * Teraz jak mamy wszystko rozwinięte to jest dużo scrollowania". Na PESA jedna wyspa ma ponad
   * 130 wpisów dostępu w kilkudziesięciu grupach — rozwinięte wszystko znaczy kilka ekranów
   * przewijania, żeby zobaczyć SAME NAZWY grup.
   *
   * ⚠️ TRZYMAMY ROZWINIĘTE, NIE ZWINIĘTE. Zbiór „co otwarte" jest pusty na start i to jest
   * dokładnie stan domyślny; zbiór „co zamknięte" wymagałby znajomości pełnej listy grup, żeby
   * powiedzieć „wszystkie" — i rozjeżdżałby się przy każdej zmianie filtrów.
   */
  const [rozwiniete, setRozwiniete] = useState<ReadonlySet<string>>(new Set());
  /** Grupa, której skład otwarto (klik w „17 osób"). */
  const [sklad, setSklad] = useState<Adresat | null>(null);

  const dane = useQuery({
    queryKey: ['permissions', groupId, search, target, mode, strona],
    queryFn: () =>
      listPermissions({ groupId: groupId!, search: search || undefined, target, mode, page: strona, pageSize: ADRESATOW_NA_STRONE }),
    enabled: Boolean(groupId),
    /** Przy przejściu między stronami zostaje poprzednia, zamiast mignąć pustym ekranem. */
    placeholderData: keepPreviousData,
  });

  /**
   * ⚠️ STRONA ZA KOŃCEM LISTY — np. po odebraniu ostatniego uprawnienia na ostatniej stronie.
   * Wracamy na ostatnią istniejącą, zamiast pokazywać „żadne uprawnienie nie pasuje".
   */
  const adresatow = dane.data?.strona?.adresatow;
  useEffect(() => {
    if (adresatow === undefined) return;
    const ostatnia = Math.max(1, Math.ceil(adresatow / ADRESATOW_NA_STRONE));
    if (strona > ostatnia) setStrona(ostatnia);
  }, [adresatow, strona]);

  const odswiez = () => void qc.invalidateQueries({ queryKey: ['permissions'] });

  /** Otwiera albo zamyka JEDNĄ sekcję. Nowy zbiór, nie mutacja — inaczej React nie przerysuje. */
  const przelaczSekcje = (klucz: string) =>
    setRozwiniete((byly) => {
      const teraz = new Set(byly);
      if (!teraz.delete(klucz)) teraz.add(klucz);
      return teraz;
    });

  /**
   * ⚠️ ZAPIS TYLKO DLA ADMINA — dokładnie tak, jak wymaga tego API (`AdminGuard`). Pokazywanie
   * przycisków, które kończą się odmową, uczy, że portal jest zepsuty.
   */
  const mozeZapisywac = user?.role === 'ADMIN';

  /** Jedna obsługa dla wszystkich akcji na wierszu — komunikat i odświeżenie w jednym miejscu. */
  const wykonaj = async (id: string, opis: string, akcja: () => Promise<unknown>) => {
    setPracuje(id);
    try {
      await akcja();
      toast.success(`${opis} — zmiana czeka w kolejce do automatu`);
      odswiez();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
    } finally {
      setPracuje(null);
    }
  };

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  const d = dane.data;
  const k = d?.konfiguracja;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-start gap-3">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Co pracownik lub grupa może pobierać. Każde uprawnienie jest bez limitu (nieograniczona ilość) albo
          z limitem (N sztuk w zadanym czasie). Dostęp i limit to dwie osobne rzeczy w automacie — limit zawęża dostęp.
        </p>
        {mozeZapisywac && (
          <Button className="ml-auto" onClick={() => setNadaje(true)}>
            <Plus className="mr-1 h-4 w-4" /> Nadaj dostęp do produktu
          </Button>
        )}
      </div>

      {nadaje && <NadajDostepModal groupId={groupId} onClose={() => setNadaje(false)} onSaved={odswiez} />}

      {sklad && <SkladGrupy adresat={sklad} onClose={() => setSklad(null)} />}

      {/*
        ⚠️⚠️ ZDJĘCIE LIMITU Z WIERSZA PODGRUPY KASUJE REGUŁĘ CAŁEJ NADGRUPY — czyli zdejmuje limit
        także tam, gdzie nikt o to nie prosił. Potwierdzenie musi to nazwać.
      */}
      {doZdjeciaLimitu !== null && doZdjeciaLimitu.u.limit !== null && groupId && (
        <OknoPotwierdzenia
          tytul="Przypisz Nolimit"
          tresc={t('perm.nolimitPytanie')
            .replace(
              '{limit}',
              `${doZdjeciaLimitu.u.limit.maxQty} szt / ${doZdjeciaLimitu.u.limit.periodLabel}`,
            )
            .replace(
              '{zakres}',
              doZdjeciaLimitu.u.zakresLimitu?.zNadgrupy === true
                ? `grupy nadrzędnej „${doZdjeciaLimitu.u.zakresLimitu.nazwa}" (CAŁEJ, nie tylko podgrupy „${doZdjeciaLimitu.u.zakres.name}")`
                : doZdjeciaLimitu.u.zakres.name,
            )
            .replace('{kto}', doZdjeciaLimitu.a.name)}
          ton="grozne"
          etykietaPotwierdz={t('perm.nolimitPotwierdz')}
          pracuje={pracuje === doZdjeciaLimitu.u.id}
          onPotwierdz={() => {
            const cel = doZdjeciaLimitu;
            setDoZdjeciaLimitu(null);
            void wykonaj(cel.u.id, `Nolimit dla „${cel.u.zakres.name}"`, () =>
              removePermissionLimit(cel.u.id, groupId),
            );
          }}
          onAnuluj={() => setDoZdjeciaLimitu(null)}
        />
      )}

      {doOdebrania !== null && groupId && (
        <OknoPotwierdzenia
          tytul="Odbierz dostęp"
          tresc={trescOdebrania(doOdebrania.u, doOdebrania.a)}
          dodatek={
            doOdebrania.a.kind === 'all' || doOdebrania.u.zakres.kind === 'all' ? (
              <SkutekOdebrania id={doOdebrania.u.id} groupId={groupId} />
            ) : undefined
          }
          ton="grozne"
          etykietaPotwierdz="Odbierz dostęp"
          pracuje={pracuje === doOdebrania.u.id}
          onPotwierdz={() => {
            const cel = doOdebrania;
            setDoOdebrania(null);
            void wykonaj(cel.u.id, `Odebrany dostęp do „${cel.u.zakres.name}"`, () =>
              revokePermission(cel.u.id, groupId),
            );
          }}
          onAnuluj={() => setDoOdebrania(null)}
        />
      )}

      {historia !== null && groupId && (
        <HistoriaLimitu
          uprawnienieId={historia.u.id}
          groupId={groupId}
          zakres={historia.u.zakres.name}
          adresat={historia.a.name}
          onClose={() => setHistoria(null)}
        />
      )}

      {doUsunieciaReguly !== null && groupId && doUsunieciaReguly.u.limit !== null && (
        <OknoPotwierdzenia
          tytul="Usuń limit"
          tresc={
            `Usunąć regułę ${doUsunieciaReguly.u.limit.maxQty} szt. / ${doUsunieciaReguly.u.limit.periodLabel} ` +
            `na „${doUsunieciaReguly.u.zakres.name}” dla „${doUsunieciaReguly.a.name}”?

` +
            'DOSTĘP NIE ZMIENIA SIĘ — ten wiersz go nie nadaje, idzie z innego wpisu. Po usunięciu ' +
            'pobieranie z tego zakresu przestaje być ograniczone dla tej osoby.'
          }
          ton="grozne"
          etykietaPotwierdz="Usuń limit"
          pracuje={pracuje === doUsunieciaReguly.u.id}
          onPotwierdz={() => {
            const cel = doUsunieciaReguly;
            setDoUsunieciaReguly(null);
            void wykonaj(cel.u.id, `Usunięcie limitu „${cel.u.zakres.name}”`, () =>
              usunLimitReguly(cel.u.id, groupId),
            );
          }}
          onAnuluj={() => setDoUsunieciaReguly(null)}
        />
      )}

      {/*
        ⚠️⚠️ NOWY LIMIT IMIENNY TU, NIE NA KARCIE PRACOWNIKA — rozstrzygnięte 07.09.2026:
        „wszystko ma być w limitach […] mamy tam już indywidualne zwykłe limity". Kreator obok nadaje
        DOSTĘP (i opcjonalnie limit); to okno nadaje SAM LIMIT komuś, kto dostęp ma już przez grupę.
      */}
      {dodajLimitDla !== null && dodajLimitDla.id !== null && groupId && (
        <OknoLimituImiennego
          groupId={groupId}
          imie={dodajLimitDla.name}
          onClose={() => setDodajLimitDla(null)}
          onZapisz={async (zakres, okno) => {
            await utworzLimitImienny(groupId, dodajLimitDla.id!, zakres, okno);
            toast.success('Limit imienny zapisany — zmiana czeka w kolejce do automatu');
            setDodajLimitDla(null);
            odswiez();
          }}
        />
      )}

      {edytowany && (
        <OknoLimituModal
          tytul={edytowany.u.limit ? 'Zmień limit' : 'Ustaw limit'}
          /**
           * ═════════════════════════════════════════════════════════════
           *  ⚠️⚠️ OPIS MUSI POWIEDZIEĆ, ŻE ZAPIS SIĘGA SZERZEJ NIŻ WIERSZ. Przy wpisie na podgrupie
           *  limit ląduje na grupie NADRZĘDNEJ — obejmuje więc także towary, których ten wpis
           *  nie dotyczy, i zużywa się z nimi wspólnie. Bez tego zdania człowiek nadaje limit
           *  szerszy, niż czyta — a widać to dopiero, gdy kogoś zablokuje przy automacie.
           * ═════════════════════════════════════════════════════════════
           */
          opis={`${
            edytowany.u.zakresLimitu?.zNadgrupy === true
              ? `Limit zapisze się na grupie nadrzędnej „${edytowany.u.zakresLimitu.nazwa}" — SPS nie wyraża limitu ` +
                `na podgrupie „${edytowany.u.zakres.name}". Obejmie CAŁĄ tę grupę, także towary spoza tego uprawnienia, ` +
                'i zużywa się z nimi wspólnie'
              : edytowany.u.zakres.name
          } dla „${edytowany.a.name}"${
            edytowany.a.kind === 'group' && edytowany.a.osob !== null
              ? ` — limit liczy się PER OSOBĘ, a grupa ma ${edytowany.a.osob} członków`
              : ''
          }`}
          poczatkowe={
            edytowany.u.limit
              ? {
                  maxQty: edytowany.u.limit.maxQty,
                  period: edytowany.u.limit.period,
                  periodType: edytowany.u.limit.periodType,
                }
              : null
          }
          onClose={() => setEdytowany(null)}
          onZapisz={async (o) => {
            /**
             * ⚠️⚠️ DWIE TRASY, JEDEN FORMULARZ. Zwykły wiersz to wpis dostępu i limit zapisuje się
             * przy nim; wiersz bez wpisu dostępu to sama reguła, więc idzie po swoim identyfikatorze.
             * Wysłanie pod zły adres trafiłoby w cudzy rekord albo w nic.
             */
            if (edytowany.u.bezDostepu === null) await setPermissionLimit(edytowany.u.id, groupId, o);
            else await zmienLimitReguly(edytowany.u.id, o);
            toast.success('Limit zapisany — zmiana czeka w kolejce do automatu');
            odswiez();
          }}
        />
      )}

      {/* ── KONFIGURACJA AUTOMATÓW ──────────────────────────────────────────────────────────
          ⚠️ NA WIERZCHU, NIE W ZWINIĘTEJ SEKCJI. Przy `REST_ENABLED = 0` cała ta lista nic nie
          robi, a przy `REST_DENY = 1` robi coś PRZECIWNEGO, niż wygląda. Operator musi to
          przeczytać zanim zacznie nadawać dostęp, a nie po. */}
      {k && (
        <div className="mb-3 rounded-lg border border-border bg-card p-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <span className="text-muted-foreground">
              Konfiguracja automatów wyspy ({k.automatow}):
            </span>
            <span className="flex items-center gap-1.5">
              Ograniczenia produktów: <PlakietkaFlagi f={k.flagi.REST_ENABLED!} />
            </span>
            <span className="flex items-center gap-1.5">
              Limity wydawania: <PlakietkaFlagi f={k.flagi.RATION_ENABLED!} />
            </span>
            <button
              type="button"
              className="ml-auto text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              onClick={() => setRozwinFlagi((v) => !v)}
            >
              {rozwinFlagi ? 'Zwiń parametry' : `Pokaż wszystkie parametry (${Object.keys(k.flagi).length})`}
            </button>
          </div>

          {/* ⚠️ `REST_DENY` OSOBNYM ZDANIEM, bo odwraca znaczenie każdego wiersza niżej. Sama
              plakietka „WŁĄCZONE" nic by tu nie powiedziała — trzeba nazwać skutek. */}
          {!k.dostepNadaje && (
            <p className="mt-2 flex items-start gap-1.5 rounded border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--warning)]" />
              <span>
                {k.flagi.REST_DENY?.stan === 'WLACZONE'
                  ? 'Na tej wyspie lista ZABIERA dostęp zamiast go nadawać (REST_DENY = 1). Każdy wiersz niżej znaczy „temu adresatowi NIE WOLNO pobierać tego produktu".'
                  : k.flagi.REST_ENABLED?.stan === 'WYLACZONE'
                    ? 'Ograniczenia produktów są w automatach WYŁĄCZONE (REST_ENABLED = 0) — wpisy niżej istnieją, ale automat ich nie czyta.'
                    : `Nie wiadomo, czy wpis nadaje dostęp, czy go zabiera: ${k.flagi.REST_DENY?.powod ?? k.flagi.REST_ENABLED?.powod ?? 'brak odczytu ustawień'}. Dopóki to się nie wyjaśni, portal mówi „istnieje wpis dostępu", a nie „ma dostęp".`}
              </span>
            </p>
          )}

          {rozwinFlagi && (
            <div className="mt-3 grid gap-x-6 gap-y-1 border-t border-border pt-2 text-xs sm:grid-cols-2">
              {Object.values(k.flagi).map((f) => (
                <div key={f.param} className="flex items-center justify-between gap-2 py-0.5">
                  <span className="truncate" title={f.opis ?? undefined}>
                    <code className="text-[11px] text-muted-foreground">{f.param}</code>
                    {f.opis && <span className="ml-2 text-muted-foreground">{f.opis}</span>}
                  </span>
                  <PlakietkaFlagi f={f} />
                </div>
              ))}
              {/* Opis pochodzi WPROST z bazy klienta i bywa po polsku, bywa po angielsku —
                  to jedyna dokumentacja tych flag, jaką mamy, więc pokazujemy ją tak, jak jest. */}
              <p className="col-span-full mt-1 text-[11px] text-muted-foreground">
                Opisy parametrów pochodzą wprost z bazy automatu (kolumna <code>Descr</code> w <code>SetupData</code>).
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── FILTRY ─────────────────────────────────────────────────────────────────────── */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="w-64 pl-8"
            placeholder="Szukaj adresata albo produktu…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setStrona(1);
            }}
          />
        </div>

        <Przelacznik
          etykieta="Komu"
          wartosc={target}
          opcje={[
            { v: 'all', l: 'wszyscy' },
            { v: 'employees', l: 'osoby' },
            { v: 'groups', l: 'grupy' },
          ]}
          zmien={(v) => {
            setTarget(v);
            setStrona(1);
          }}
        />
        <Przelacznik
          etykieta="Tryb"
          wartosc={mode}
          opcje={[
            { v: 'all', l: 'wszystkie' },
            { v: 'nolimit', l: 'bez limitu' },
            { v: 'limit', l: 'z limitem' },
          ]}
          zmien={(v) => {
            setMode(v);
            setStrona(1);
          }}
        />

        {/* ⚠️ MIANOWNIK JEST CZĘŚCIĄ ODPOWIEDZI. „51 uprawnień" nie mówi, czy to dużo — „51 z 115"
            mówi. Licznik po prawej liczy CAŁĄ wyspę, nie przefiltrowany podzbiór. */}
        {d && (
          <span className="ml-auto text-sm text-muted-foreground">
            {d.widocznych === d.wszystkich ? (
              <>{d.wszystkich} uprawnień</>
            ) : (
              <>
                {d.widocznych} z {d.wszystkich} uprawnień
              </>
            )}
            <span className="ml-2 text-xs">
              (z limitem {d.zLimitem}, bez limitu {d.bezLimitu})
            </span>
          </span>
        )}
      </div>

      {dane.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
        </p>
      )}
      {dane.isError && <p className="text-sm text-[var(--destructive)]">{(dane.error as Error).message}</p>}

      {/* ── OSTRZEŻENIA ────────────────────────────────────────────────────────────────────
          Oba są o wierszach, których ekran NIE pokazuje jako zwykłych uprawnień. Cicho pominięty
          wiersz mówiłby, że ktoś dostępu nie ma — a to najgorsza pomyłka na tym ekranie. */}
      {/*
        ════════════════════════════════════════════════════════════════════
         ⚠️⚠️ CO TU LĄDUJE PO ZMIANIE Z 07.09.2026: wyłącznie reguły MARTWE oraz te o adresacie
         poza modelem portalu (typ 3-6: zadanie, automat). Reguły, które DZIAŁAJĄ innym poziomem
         dostępu, są teraz WIERSZAMI w sekcji swojego adresata — bo tam się je edytuje.

         ⚠️⚠️ DWIE RÓŻNE RZECZY, ROZDZIELONE 07.09.2026. Do tego dnia stało tu jedno zdanie
         — „reguł limitu bez wpisu dostępu — automat nie ma czego ograniczać" — i obejmowało
         każdą regułę, której nie dało się zestawić z wierszem o IDENTYCZNEJ parze (kto, co).
         Na EMCFIME taka „martwa" reguła zablokowała człowieka na 11 towarów do 28.07.2027, bo
         dostęp szła przez jego grupę i na podgrupę towarową. Fałszywe ostrzeżenie jest gorsze
         od braku ostrzeżenia: kazało szukać przyczyny gdzie indziej.
        ════════════════════════════════════════════════════════════════════
      */}
      {d && d.limityBezDostepu.some((l) => !l.martwy) && (
        <div className="mb-3 rounded-lg border border-border bg-muted/30 p-3 text-sm">
          <p className="flex items-center gap-1.5 font-medium">
            <Info className="h-4 w-4 text-muted-foreground" />
            {d.limityBezDostepu.filter((l) => !l.martwy).length} limitów o adresacie poza modelem portalu — nie oceniamy, czy działają
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {d.limityBezDostepu
              .filter((l) => !l.martwy)
              .slice(0, 8)
              .map((l) => (
                <li key={l.id}>
                  <span className="font-medium">{l.cel}</span> → {l.zakres} · {l.powod}
                </li>
              ))}
            {d.limityBezDostepu.filter((l) => !l.martwy).length > 8 && <li>…</li>}
          </ul>
        </div>
      )}
      {d && d.limityBezDostepu.some((l) => l.martwy) && (
        <div className="mb-3 rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-sm">
          <p className="flex items-center gap-1.5 font-medium">
            <AlertTriangle className="h-4 w-4 text-[var(--warning)]" />
            {d.limityBezDostepu.filter((l) => l.martwy).length} limitów bez widocznego dostępu — każdy stoi też przy swoim adresacie
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {d.limityBezDostepu
              .filter((l) => l.martwy)
              .slice(0, 8)
              .map((l) => (
                <li key={l.id}>
                  <span className="font-medium">{l.cel}</span> → {l.zakres} · {l.powod}
                </li>
              ))}
            {d.limityBezDostepu.filter((l) => l.martwy).length > 8 && <li>…</li>}
          </ul>
        </div>
      )}
      {d && d.nierozpoznane.length > 0 && (
        <div className="mb-3 rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-sm">
          <p className="flex items-center gap-1.5 font-medium">
            <AlertTriangle className="h-4 w-4 text-[var(--warning)]" />
            {d.nierozpoznane.length} wpisów dostępu o nierozpoznanej masce
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {d.nierozpoznane.slice(0, 8).map((n) => (
              <li key={n.id}>
                <code>
                  {n.rUser} → {n.rItem}
                </code>{' '}
                · {n.powod}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        ⚠️ NAD LISTĄ, NAD „nierozpoznanymi maskami" — bo to jedyna rzecz na tym ekranie, która
        znaczy „ktoś PRAWDOPODOBNIE dzwoni w tej chwili". Nierozpoznana maska jest zagadką dla nas;
        zderzenie limitu z pełnym wydaniem jest odmową dla pracownika stojącego przy automacie.
      */}
      {groupId !== null && <ZderzeniaLimitow groupId={groupId} mozeZapisywac={mozeZapisywac} />}

      {/* ── LISTA ──────────────────────────────────────────────────────────────────────── */}
      {d && d.grupy.length === 0 && !dane.isLoading && (
        <p className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
          {d.wszystkich === 0
            ? 'Na tej wyspie nie ma ani jednego wpisu dostępu do produktów.'
            : 'Żadne uprawnienie nie pasuje do filtrów.'}
        </p>
      )}

      <div className="space-y-3">
        {d?.grupy.map((g) => {
          const klucz = `${g.adresat.kind}:${g.adresat.spsKey}`;
          /**
           * ⚠️ PRZY AKTYWNYM FILTRZE SEKCJE SĄ OTWARTE Z URZĘDU. Filtr zostawia w grupie WYŁĄCZNIE
           * pasujące wiersze, więc zwinięcie kazałoby klikać każdą grupę po kolei, żeby zobaczyć to,
           * o co się właśnie zapytało — czyli szukanie i filtry przestałyby odpowiadać.
           *
           * ⚠️ LICZY SIĘ KAŻDE ZAWĘŻENIE, nie tylko szukajka: „tylko z limitem" jest pytaniem o to
           * samo, co wpisanie nazwy, tylko zadanym przyciskiem.
           */
          const zawezone = search.trim() !== '' || mode !== 'all' || target !== 'all';
          const otwarta = zawezone || rozwiniete.has(klucz);
          const zLimitem = g.uprawnienia.filter((u) => u.limit !== null).length;
          return (
          <section key={klucz} className="rounded-lg border border-border bg-card">
            {/*
              ⚠️ NAGŁÓWEK MA WŁASNE TŁO, bo bez niego nazwa adresata czytała się jak jeszcze jeden
              wiersz uprawnienia — zgłoszone z ekranu klienta. Tło (a nie sama grubsza czcionka)
              rozdziela listę na „kto" i „co", zanim ktokolwiek zacznie czytać treść.
            */}
            <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-t-lg border-b border-border bg-muted/60 px-3 py-2">
              {/*
                ⚠️ PRZEŁĄCZNIK OBEJMUJE NAZWĘ I OPIS, ale NIE liczbę osób — bo ta jest osobnym
                przyciskiem, a przycisk w przycisku jest niepoprawnym HTML-em i w części przeglądarek
                przestaje reagować na klik.
              */}
              <button
                type="button"
                onClick={() => przelaczSekcje(klucz)}
                aria-expanded={otwarta}
                className="flex min-w-0 flex-1 items-baseline gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronRight
                  className={cn('mt-0.5 size-3.5 shrink-0 transition-transform', otwarta && 'rotate-90')}
                  aria-hidden
                />
                <h3 className="font-semibold">{g.adresat.name}</h3>
                <span className="text-xs text-muted-foreground">
                  {ETYKIETA_ADRESATA[g.adresat.kind]}
                  {' · '}
                  {zLiczba(g.uprawnienia.length, UPRAWNIENIA)}
                  {/*
                    ⚠️ ILE Z LIMITEM — WIDOCZNE TAKŻE PRZY ZWINIĘTEJ SEKCJI. Zwinięcie ukrywa
                    plakietki limitów, czyli to, po co się na ten ekran wchodzi; bez tej liczby
                    trzeba by rozwinąć każdą grupę po kolei, żeby sprawdzić, czy limity są.
                  */}
                  {zLimitem > 0 && ` · z limitem: ${zLimitem}`}
                </span>
              </button>
              {/*
                LICZBA OSÓB JEST PRZYCISKIEM — patrz `SkladGrupy`. Podkreślenie kropkowane, bo to
                nie jest odnośnik do innej strony, a jednak trzeba pokazać, że da się kliknąć.
              */}
              {g.adresat.kind === 'group' && g.adresat.osob !== null && g.adresat.id !== null ? (
                <button
                  type="button"
                  onClick={() => setSklad(g.adresat)}
                  className="flex items-center gap-1 rounded text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  title="Pokaż, kto należy do tej grupy"
                >
                  <UserRound className="size-3" aria-hidden />
                  {zLiczba(g.adresat.osob, OSOBY)}
                </button>
              ) : (
                g.adresat.osob !== null && (
                  <span className="text-xs text-muted-foreground">{zLiczba(g.adresat.osob, OSOBY)}</span>
                )
              )}
              {/* Kod SPS obok nazwy: to po nim operator znajdzie ten wiersz w SPSUtilities. */}
              <code className="text-[11px] text-muted-foreground">{g.adresat.spsKey}</code>

              {/*
                ═══════════════════════════════════════════════════════
                 ⚠️⚠️ LIMIT IMIENNY NADAJE SIĘ PRZY OSOBIE, w jej sekcji — zamówione 07.09.2026:
                 „wszystko ma być w limitach". Kreator na górze ekranu nadaje DOSTĘP (i opcjonalnie
                 limit przy nim); to jest druga, brakująca droga: SAM LIMIT dla kogoś, kto dostęp
                 ma już przez grupę. Bez niej takiej reguły nie dało się w portalu założyć wcale.

                 ⚠️ TYLKO PRZY OSOBIE ZNANEJ PORTALOWI (`id !== null`): reguła wiąże się
                 z identyfikatorem pracownika, a nie z samym kluczem SPS.
                ═══════════════════════════════════════════════════════
              */}
              {mozeZapisywac && g.adresat.kind === 'employee' && g.adresat.id !== null && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto h-7"
                  title="Nadaje sam limit — dostęp zostaje bez zmian"
                  onClick={() => setDodajLimitDla(g.adresat)}
                >
                  <Plus className="size-3.5" />
                  Limit imienny
                </Button>
              )}
            </header>

            {/* Wiersze wcięte od lewej — nazwa produktu ma być WIDOCZNIE pod nagłówkiem, a nie
                w tej samej kolumnie co nazwa adresata. */}
              {/*
                ⚠️ SIATKA Z REZERWOWANĄ SZEROKOŚCIĄ NA NAZWĘ, a nie zwykły `flex`.
                ══════════════════════════════════════════════════════════════════════════════
                 Zgłoszone (28.08.2026): „ustaw tak, by limity były w jednej linii, bo teraz to
                 się ciężko czyta". Przy `flex` plakietka limitu startowała tam, gdzie skończyła
                 się nazwa — a nazwy mają od 6 do 32 znaków, więc kolumna limitów była poszarpana
                 i przy 30 wierszach nie dawała się przebiec wzrokiem.

                 ⚠️ SZEROKOŚĆ WZIĘTA Z POMIARU, NIE Z OKA (bazy PESA, 28.08.2026): kody grup
                 produktów mają najwyżej 16 znaków (`FILTR 3M P1 5911`), ale w tej samej kolumnie
                 staje też POJEDYNCZY PRODUKT, a `Items.ITEMNUMBER` dochodzi do 32
                 (`REKAWICE SLUSARSKIE SKORZANE R10`). 24rem mieści 32 znaki razem z dopiskiem
                 rodzaju; dłuższa nazwa przycina się z `title`, zamiast rozpychać kolumnę.

                 ⚠️ KOLUMNA AKCJI JEST TRZECIA I ELASTYCZNA. Gdyby to ona miała stałą szerokość,
                 a nazwa `1fr`, plakietki znów by się rozjechały — bo wierszy bez przycisków
                 (zawężone, podgrupy) jest na tym ekranie sporo.

                 ⚠️ PONIŻEJ `sm` wracamy do `flex flex-wrap`: na telefonie 24rem to cała
                 szerokość ekranu i siatka wypchnęłaby limit za krawędź.
                ══════════════════════════════════════════════════════════════════════════════
              */}
            {otwarta && (
            <ul className="divide-y divide-border">
              {g.uprawnienia.map((u) => (
                <li
                  key={u.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 pl-6 pr-3 text-sm sm:grid sm:grid-cols-[24rem_auto_minmax(0,1fr)]"
                >
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate font-medium" title={u.zakres.name}>
                      {u.zakres.name}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      ({ETYKIETA_ZAKRESU[u.zakres.kind]})
                    </span>
                  </span>

                  <span className="flex items-center gap-2">
                  {/*
                    ⚠️⚠️ ZAMÓWIONE 07.09.2026: „jeżeli to jest podgrupa to daj tylko info, że dane
                    uprawnienie jest z nadgrupy". Bez tego napisu wiersz podgrupy pokazywałby limit
                    jak swój własny — a on obowiązuje CAŁĄ grupę nadrzędną i wspólnie z nią się zużywa.
                  */}
                  {/*
                    ⚠️⚠️ WIERSZ BEZ WPISU DOSTĘPU — to sama reguła limitu, wstawiona do sekcji swojego
                    adresata, żeby dało się ją tu edytować. Bez tej plakietki wyglądałaby na zwykłe
                    uprawnienie i ktoś szukałby, komu odebrać dostęp, którego ten wiersz nie nadaje.
                  */}
                  {u.bezDostepu !== null &&
                    (u.bezDostepu.przez !== null ? (
                      <Badge
                        variant="neutral"
                        title={`Sama reguła limitu — nie ma własnego wpisu dostępu. Działa przez dostęp ${u.bezDostepu.przez}.`}
                      >
                        bez wpisu dostępu · działa przez {u.bezDostepu.przez}
                      </Badge>
                    ) : (
                      /*
                        ⚠️⚠️ WIERSZ MUSI BYĆ TU, CHOĆ NIC NIE OGRANICZA — zgłoszone 08.09.2026:
                        „nigdzie nie mogę na tym ziomku znaleźć uprawnień na <grupę> […] a tego
                        nie ma nigdzie". Limit stał na tej osobie, a w jej sekcji go nie
                        było: wylądował w żółtym pasku na górze, przyciętym do ośmiu pozycji.
                        Zmierzone potem w bazie: dostęp do <grupa> mają inne osoby i inne grupy —
                        ta jedna go po prostu nie ma, więc limit nie ma czego ograniczać. Ale to
                        WNIOSEK, który człowiek musi móc przeczytać przy wierszu, a nie zgadywać
                        z jego nieobecności.
                      */
                      <Badge variant="warning" title={u.bezDostepu.coWiemy}>
                        brak dostępu do tego zakresu
                      </Badge>
                    ))}
                  {/* Powód wprost pod plakietką — tytuł na dymku czyta tylko ten, kto tam trafi. */}
                  {u.bezDostepu?.przez === null && (
                    <span className="text-xs text-muted-foreground">{u.bezDostepu.coWiemy}</span>
                  )}

                  {u.zakresLimitu?.zNadgrupy === true && (
                    <Badge
                      variant="neutral"
                      title={
                        `Limit tego wiersza siedzi na grupie nadrzędnej „${u.zakresLimitu.nazwa}" — SPS nie wyraża ` +
                        'limitu na podgrupie. Obowiązuje więc całą tę grupę i zużywa się wspólnie z jej pozostałymi towarami.'
                      }
                    >
                      z nadgrupy {u.zakresLimitu.nazwa}
                    </Badge>
                  )}

                  {u.limit ? (
                    <Badge variant="default" title={u.limit.unenforced ? 'Automat NIE egzekwuje okresów miesięcznych i rocznych' : undefined}>
                      {u.limit.maxQty} szt / {u.limit.periodLabel}
                      {u.limit.unenforced ? ' (automat tego nie pilnuje)' : ''}
                    </Badge>
                  ) : (
                    /* ⚠️ PLAKIETKA, NIE SZARY NAPIS. „Bez limitu" jest tak samo ważnym stanem jak
                       „5 szt / dzień" — a jako najjaśniejszy tekst w wierszu ginął przy pierwszym
                       przebiegu wzrokiem. Wariant `neutral` (ramka + tło), żeby czytało się jako
                       stan, ale nie krzyczało głośniej niż wpis z limitem. */
                    <Badge variant="neutral" title="Dostęp bez ograniczenia ilości">
                      bez limitu
                    </Badge>
                  )}
                  {/*
                    ZEGAREK Z HISTORIĄ ZMIAN LIMITU — zamówiony 02.09.2026.

                    ⚠️ TYLKO TAM, GDZIE PORTAL COŚ ZMIENIAŁ. Ikonka przy każdym wierszu byłaby
                    kolejnym elementem do zignorowania; przy trzech wierszach z osiemdziesięciu
                    mówi, gdzie warto zajrzeć.

                    ⚠️ STOI TAKŻE PRZY „BEZ LIMITU", i to jest ten najważniejszy przypadek:
                    wiersz bez limitu z historią znaczy, że ktoś limit ZDJĄŁ — a okienko mówi,
                    ile go było. Bez tego „Przypisz Nolimit" zabierałby liczbę bez śladu.

                    ⚠️ OSOBNY PRZYCISK, NIE KLIKALNA PLAKIETKA. Plakietka nie wygląda na klikalną
                    i nie powinna: po niej czyta się limit, a nie otwiera okna.
                  */}
                  {u.zmianPortalu > 0 && (
                    <button
                      type="button"
                      onClick={() => setHistoria({ u, a: g.adresat })}
                      title={t('hist.dymek').replace('{n}', String(u.zmianPortalu))}
                      aria-label={t('hist.dymek').replace('{n}', String(u.zmianPortalu))}
                      className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      <History className="size-3.5" aria-hidden />
                    </button>
                  )}
                  </span>

                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {/* Nazwa nierozwiązana = kod z SPS wskazuje coś, czego nie ma w read-modelu.
                      Wtedy `name` jest równe `spsKey` i trzeba to powiedzieć, a nie udawać nazwę. */}
                  {u.zakres.id === null && u.zakres.kind !== 'all' && (
                    <Badge variant="warning" title="Kod z SPS nie wskazuje żadnego produktu ani grupy w read-modelu">
                      nieznany kod
                    </Badge>
                  )}

                  {u.pending && (
                    <Badge variant="warning" title="Zmiana czeka w kolejce do automatu">
                      <Clock className="mr-1 h-3 w-3" /> w kolejce
                    </Badge>
                  )}

                  {/* ⚠️ WIERSZ ZAWĘŻONY = TYLKO ODCZYT. Nasz model uprawnienia to para (kto, co),
                      a taki wiersz ma jeszcze trzeci wymiar (zadanie albo automat), którego ekran
                      nie widzi — „odbierz dostęp" skasowałoby regułę węższą, niż operator czyta. */}
                  {u.zawezone.length > 0 && (
                    <Badge
                      variant="warning"
                      title={`Wiersz zawężony dodatkowo przez: ${u.zawezone.join(', ')}. Portal go nie zmienia — zrób to w SPSUtilities.`}
                    >
                      <Lock className="mr-1 h-3 w-3" /> zawężony ({u.zawezone.join(', ')})
                    </Badge>
                  )}

                  {/* ── AKCJE ──────────────────────────────────────────────────────────────
                      Kolejność od najłagodniejszej do najostrzejszej, a „Odbierz dostęp" na
                      końcu i na czerwono — bo jako jedyna zabiera człowiekowi możliwość
                      pobrania towaru. Wiersz zawężony nie dostaje ich wcale: nasz model to
                      para (kto, co), a taki wpis ma jeszcze trzeci wymiar. */}
                  {/*
                    ⚠️ WPIS „WSZYSCY" / „WSZYSTKIE PRODUKTY" DOSTAJE SAMO „ODBIERZ DOSTĘP" (28.09.2026).
                    Limitu na „wszystkich" tabela limitów SPS nie zapisze, więc przycisków limitu tu nie ma.
                  */}
                  {mozeZapisywac && u.zawezone.length === 0 && (
                    <span className="ml-auto flex flex-wrap items-center gap-1">
                      {pracuje === u.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                      {g.adresat.kind !== 'all' && u.zakres.kind !== 'all' && (
                      <>
                      {/*
                        ═══════════════════════════════════════════════════════
                         ⚠️⚠️ DECYZJA ZMIENIONA 07.09.2026. Stał tu napis „limit niedostępny (podgrupa)"
                         — prawdziwy o ZAPISIE (`Rationing.ITEMTYPE` zna tylko towar i grupę główną)
                         i nieprawdziwy o SKUTKU: limit z grupy NADRZĘDNEJ działa na towary
                         podgrupy. Zmierzone na EMCFIME — blokada 11 towarów do 28.07.2027 przy
                         wierszu, który wyglądał na nielimitowany.
                         Teraz wiersz podgrupy edytuje limit NADGRUPY, a plakietka mówi, że to
                         nie ten poziom.
                        ═══════════════════════════════════════════════════════
                      */}
                      {u.zakresLimitu === null ? (
                        <span
                          className="text-xs text-muted-foreground"
                          title="Zakres tego wpisu nie ma odpowiednika w tabeli Rationing, więc limitu nie ma gdzie zapisać"
                        >
                          limitu nie ma gdzie zapisać
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          disabled={pracuje !== null}
                          title={
                            u.zakresLimitu.zNadgrupy
                              ? `Limit zapisze się na grupie nadrzędnej „${u.zakresLimitu.nazwa}" i obejmie CAŁĄ tę grupę, ` +
                                'także towary spoza tego uprawnienia — SPS nie wyraża limitu na podgrupie.'
                              : undefined
                          }
                          onClick={() => setEdytowany({ u, a: g.adresat })}
                        >
                          {u.limit ? 'Zmień limit' : 'Ustaw limit'}
                        </Button>
                      )}
                      {u.limit && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          disabled={pracuje !== null}
                          title="Zdejmuje limit, ale ZOSTAWIA dostęp — po tej zmianie pobieranie jest nieograniczone"
                          onClick={() => setDoZdjeciaLimitu({ u, a: g.adresat })}
                        >
                          Przypisz Nolimit
                        </Button>
                      )}
                      </>
                      )}
                      {/*
                        ⚠️⚠️ „ODBIERZ DOSTĘP" TYLKO PRZY PRAWDZIWYM WPISIE DOSTĘPU. Wiersz bez wpisu
                        nie nadaje dostępu — ten idzie z innego wiersza i jego odebranie dotknęłoby
                        wszystkich, których tamten obejmuje. Zamiast tego wolno usunąć samą regułę.
                      */}
                      {u.bezDostepu === null ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 border-[var(--destructive)]/40 text-[var(--destructive)] hover:bg-[var(--destructive)]/10"
                          disabled={pracuje !== null}
                          onClick={() => setDoOdebrania({ u, a: g.adresat })}
                        >
                          Odbierz dostęp
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 border-[var(--destructive)]/40 text-[var(--destructive)] hover:bg-[var(--destructive)]/10"
                          disabled={pracuje !== null}
                          title="Usuwa samą regułę limitu. Dostęp zostaje — idzie z innego wiersza."
                          onClick={() => setDoUsunieciaReguly({ u, a: g.adresat })}
                        >
                          Usuń limit
                        </Button>
                      )}
                    </span>
                  )}
                  </span>
                </li>
              ))}
            </ul>
            )}
          </section>
          );
        })}
      </div>

      {d?.strona && d.strona.adresatow > d.strona.pageSize && (
        <Strony
          strona={d.strona.page}
          naStronie={d.strona.pageSize}
          adresatow={d.strona.adresatow}
          laduje={dane.isFetching}
          zmien={setStrona}
        />
      )}

      {d && d.wszystkich > 0 && (
        <p className="mt-4 flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Dostęp pochodzi z tabeli <code>Restrictions2</code>, limit z <code>Rationing</code> — w automacie to dwa
            osobne mechanizmy, tu jeden widok. Limit bez wpisu dostępu nie ma czego ograniczać, dlatego nadanie limitu
            zawsze zakłada najpierw dostęp. Wyspa: {island?.name ?? '—'}.
          </span>
        </p>
      )}
    </>
  );
}

/** Przełącznik filtra — trzy stany obok siebie czytają się szybciej niż lista rozwijana. */
function Przelacznik<T extends string>({
  etykieta,
  wartosc,
  opcje,
  zmien,
}: {
  etykieta: string;
  wartosc: T;
  opcje: { v: T; l: string }[];
  zmien: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <span className="text-muted-foreground">{etykieta}:</span>
      <div className="flex overflow-hidden rounded-md border border-border">
        {opcje.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => zmien(o.v)}
            className={
              'px-2.5 py-1 text-xs transition-colors ' +
              (wartosc === o.v ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'hover:bg-muted')
            }
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}
